import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  TableHead,
} from '@/components/ui/table';
import { StatusBadge as AppStatusBadge } from '@/components/ui/status-badge';
import { Membre, getAttestationStatus } from '@/hooks/queries/useMembresQuery';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ConformiteTableProps {
  membres: Membre[];
  loading: boolean;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy', { locale: fr });
  } catch {
    return '—';
  }
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: fr }) + ' UTC';
  } catch {
    return '—';
  }
}

function StatusBadge({ status }: { status: ReturnType<typeof getAttestationStatus> }) {
  switch (status) {
    case 'conforme':
      return <AppStatusBadge tone="success">Conforme</AppStatusBadge>;
    case 'en_attente':
      return <AppStatusBadge tone="warning">En attente</AppStatusBadge>;
    case 'verification_en_cours':
      return <AppStatusBadge tone="info">Vérification en cours</AppStatusBadge>;
    case 'expiree':
      return <AppStatusBadge tone="danger">Expirée</AppStatusBadge>;
    case 'a_regulariser':
      return <AppStatusBadge tone="danger">À régulariser</AppStatusBadge>;
  }
}

/**
 * Tableau de conformité légal — destiné à être exporté en PDF
 * Colonnes : Nom, Contrat, Embauche, Date demande att., Date réception, Statut, Renouvellement
 */
export const ConformiteTable = ({ membres, loading }: ConformiteTableProps) => {
  // Trier : à régulariser en premier, puis en attente/vérification, puis conforme
  const sortedMembres = [...membres].sort((a, b) => {
    const order: Record<ReturnType<typeof getAttestationStatus>, number> = {
      a_regulariser: 0,
      expiree: 0,
      en_attente: 1,
      verification_en_cours: 1,
      conforme: 2,
    };
    const statusA = getAttestationStatus(a);
    const statusB = getAttestationStatus(b);
    return (order[statusA] ?? 3) - (order[statusB] ?? 3);
  });

  return (
    <div className="overflow-hidden rounded-[18px] border border-[hsl(var(--app-border-subtle))] bg-white">
      {/* En-tête du document */}
      <div className="flex items-center justify-between border-b border-[hsl(var(--app-border-subtle))] px-4 py-3">
        <p className="text-[12px] leading-5 text-[hsl(var(--app-text-muted))]">
          Généré le {format(new Date(), "d MMMM yyyy 'à' HH:mm", { locale: fr })} · contrôle administratif
        </p>
        <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-emerald-700">
          Document légal
        </span>
      </div>

      {/* Tableau */}
      <Table>
        <TableHeader>
          <TableRow className="border-b border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-muted))] hover:bg-[hsl(var(--app-surface-muted))]">
            <TableHead className="h-11 text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">Nom</TableHead>
            <TableHead className="h-11 text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">Contrat</TableHead>
            <TableHead className="h-11 text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">Embauche</TableHead>
            <TableHead className="h-11 text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">Demande att.</TableHead>
            <TableHead className="h-11 text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">Réception</TableHead>
            <TableHead className="h-11 text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">Vérifiée le</TableHead>
            <TableHead className="h-11 text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">Statut</TableHead>
            <TableHead className="h-11 text-[11px] font-semibold uppercase tracking-[0.14em] text-[hsl(var(--app-text-muted))]">Renouvellement</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={`skeleton-${i}`}>
                {Array.from({ length: 8 }).map((_, j) => (
                  <TableCell key={j}>
                    <div className="h-4 w-20 animate-pulse rounded bg-[hsl(var(--app-surface-muted))]" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : sortedMembres.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="py-12 text-center text-[13px] text-[hsl(var(--app-text-muted))]">
                Aucun membre dans l'équipe.
              </TableCell>
            </TableRow>
          ) : (
            sortedMembres.map(membre => {
              const status = getAttestationStatus(membre);
              const contratLabel = membre.type_contrat === 'Benevole' ? 'Bénévole' : membre.type_contrat === 'Interim' ? 'Intérim' : membre.type_contrat;
              
              return (
                <TableRow key={membre.id} className="border-b border-[hsl(var(--app-border-subtle))] last:border-b-0 hover:bg-[hsl(var(--app-surface-muted))]/40">
                  <TableCell className="py-3.5 font-medium text-[hsl(var(--app-text-strong))]">
                    {membre.nom} {membre.prenom}
                  </TableCell>
                  <TableCell className="py-3.5 text-[13px] text-[hsl(var(--app-text))]">{contratLabel}</TableCell>
                  <TableCell className="py-3.5 text-[13px] text-[hsl(var(--app-text))]">{formatDate(membre.date_embauche)}</TableCell>
                  <TableCell className="py-3.5 text-[13px] text-[hsl(var(--app-text))]">{formatDate(membre.created_at)}</TableCell>
                  <TableCell className="py-3.5 text-[13px] text-[hsl(var(--app-text))]">
                    {formatDate(membre.attestation?.received_at ?? null)}
                  </TableCell>
                  <TableCell className="py-3.5 text-[13px] text-[hsl(var(--app-text))]">
                    {formatDateTime(membre.attestation?.verification_verified_at ?? null)}
                  </TableCell>
                  <TableCell className="py-3.5"><StatusBadge status={status} /></TableCell>
                  <TableCell className="py-3.5">
                    {membre.attestation?.date_renouvellement ? (
                      <span className={
                        new Date(membre.attestation.date_renouvellement) < new Date()
                          ? 'font-medium text-rose-600'
                          : 'text-[13px] text-[hsl(var(--app-text))]'
                      }>
                        {formatDate(membre.attestation.date_renouvellement)}
                        {new Date(membre.attestation.date_renouvellement) < new Date() && ' ⚠'}
                      </span>
                    ) : (
                      <span className="text-[13px] text-[hsl(var(--app-text-muted))]">—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Footer légal */}
      <div className="border-t border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-muted))] px-4 py-3">
        <p className="text-[11px] leading-5 text-[hsl(var(--app-text-muted))]">
          Conservation : 3 ans en base active + 5 ans en archivage intermédiaire après le départ du salarié. Horodatage GetProof : {format(new Date(), "dd/MM/yyyy 'à' HH:mm:ss", { locale: fr })} UTC.
        </p>
      </div>
    </div>
  );
};
